import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { getQuestion, voteQuestion } from "@/lib/academy.functions";
import { getOrCreateSessionId } from "@/lib/academy-session";

export const Route = createFileRoute("/academy/ask/$id")({
  head: () => ({
    meta: [{ title: "Answered — Ask Currency Thoughts" }],
  }),
  loader: async ({ params, context }) => {
    const d = await context.queryClient.ensureQueryData({
      queryKey: ["academy", "question", params.id],
      queryFn: () => getQuestion({ data: { id: params.id } }),
    });
    if (!d) throw notFound();
  },
  component: QuestionPage,
  notFoundComponent: () => (
    <div>
      <Navbar />
      <div className="mx-auto max-w-2xl px-5 py-32 text-center">
        <h1 className="font-display text-3xl text-navy-900">Question not found</h1>
        <Link to="/academy/ask" className="mt-6 inline-block text-bronze-600">
          Back to Q&A
        </Link>
      </div>
      <Footer />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div>
      <Navbar />
      <div className="mx-auto max-w-2xl px-5 py-32 text-center">
        <p>{error.message}</p>
      </div>
      <Footer />
    </div>
  ),
});

function QuestionPage() {
  const { id } = Route.useParams();
  const { data } = useSuspenseQuery({
    queryKey: ["academy", "question", id],
    queryFn: () => getQuestion({ data: { id } }),
  });
  const voteFn = useServerFn(voteQuestion);
  const [vote, setVote] = useState<"up" | "down" | null>(null);
  const [sessionId, setSessionId] = useState("");

  useEffect(() => {
    setSessionId(getOrCreateSessionId());
  }, []);

  if (!data) return null;
  const { question, up, down } = data;
  const cleanedQ = (question.question ?? "").replace(/^\[.*?\]\n\n/s, "");

  async function cast(v: "up" | "down") {
    if (!sessionId) return;
    setVote(v);
    try {
      await voteFn({ data: { id, sessionId, vote: v } });
      toast.success("Thanks for the feedback.");
    } catch (err) {
      setVote(null);
      toast.error(err instanceof Error ? err.message : "Could not vote.");
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-3xl px-5 py-16 md:px-10 md:py-20">
            <Link
              to="/academy/ask"
              className="inline-flex items-center gap-2 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-400 hover:text-cream-50"
            >
              <ArrowLeft className="h-3 w-3" /> Back to Q&A
            </Link>
            <p className="mt-6 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-400">
              The Question
            </p>
            <h1 className="mt-3 font-display text-[32px] leading-snug md:text-[44px]">
              {cleanedQ}
            </h1>
          </div>
        </section>

        <section className="bg-cream-50 py-16">
          <div className="mx-auto grid max-w-5xl gap-12 px-5 md:px-10 lg:grid-cols-[1fr_240px]">
            <article className="max-w-[680px]">
              <Eyebrow>Our Answer</Eyebrow>
              <div className="mt-6 font-sans text-[17px] leading-[1.75] text-charcoal-900 [&_p]:mb-5">
                <ReactMarkdown>{question.answer ?? ""}</ReactMarkdown>
              </div>

              <div className="mt-12 border-t border-bronze-600/30 pt-8">
                <p className="font-sans text-[13px] font-semibold text-navy-900">
                  Was this helpful?
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={() => cast("up")}
                    disabled={!!vote}
                    className={`inline-flex items-center gap-2 border px-4 py-2 font-sans text-[13px] ${
                      vote === "up"
                        ? "border-bronze-600 bg-bronze-600/10 text-navy-900"
                        : "border-bronze-600/30 text-charcoal-900 hover:border-bronze-600"
                    }`}
                  >
                    <ThumbsUp className="h-4 w-4" /> {up + (vote === "up" ? 1 : 0)}
                  </button>
                  <button
                    onClick={() => cast("down")}
                    disabled={!!vote}
                    className={`inline-flex items-center gap-2 border px-4 py-2 font-sans text-[13px] ${
                      vote === "down"
                        ? "border-bronze-600 bg-bronze-600/10 text-navy-900"
                        : "border-bronze-600/30 text-charcoal-900 hover:border-bronze-600"
                    }`}
                  >
                    <ThumbsDown className="h-4 w-4" /> {down + (vote === "down" ? 1 : 0)}
                  </button>
                </div>
              </div>

              <div className="mt-12 bg-navy-900 p-8 text-cream-50" style={{ borderRadius: 6 }}>
                <Eyebrow color="cream">Your Turn</Eyebrow>
                <h3 className="mt-4 font-display text-[24px]">
                  Have a question of your own?
                </h3>
                <p className="mt-2 font-sans text-[14px] text-cream-50/80">
                  We answer one every week. Free.
                </p>
                <PrimaryButton asChild className="mt-5">
                  <Link to="/academy/ask">Submit your question</Link>
                </PrimaryButton>
              </div>
            </article>

            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div className="border-l-2 border-bronze-600/60 pl-4">
                <p className="font-sans text-[11px] uppercase tracking-[0.18em] text-bronze-600">
                  Compliance
                </p>
                <p className="mt-2 font-sans text-[12px] leading-relaxed text-charcoal-500">
                  Answers are educational and reflect general principles. They are not legal,
                  tax, or investment advice and don't constitute a recommendation. Talk to a
                  licensed Strategist about your specific situation.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
